import { useMutation } from '@apollo/client/react'
import { Badge, Box, Button, Separator, Heading, Link, Text, VStack, Dialog, Field } from '@chakra-ui/react'
import { AutoResizeTextarea } from '@src/components/sco/AutoResizeTextarea'
import { ComboboxItem } from '@src/components/sco/ComboboxTagInput'
import { LocationInput } from '@src/components/sco/LocationInput'
import { UserSearchEmailToggle } from '@src/components/sco/Matching/UserSearchEmailToggle'
import { USER_SEARCH_FRAGMENT } from '@src/components/sco/Matching/userSearchFragment'
import { SkillsComboboxInput } from '@src/components/sco/SkillsComboboxInput'
import { ScoUserSearch } from '@src/graphql-types'
import { gql } from '@src/lib/gql'
import { sample } from 'lodash'
import { useRouter } from 'next/navigation'
import React, { useEffect, useState } from 'react'

export const UserSearchForm = (props: { userSearch?: ScoUserSearch }) => {
  const router = useRouter()
  const [title, setTitle] = useState<string>(props.userSearch?.title)
  const [naturalLanguagePrompt, setNaturalLanguagePrompt] = useState(props.userSearch?.naturalLanguagePrompt)
  const [requiredSkills, setRequiredSkills] = useState<ComboboxItem[]>(
    props.userSearch?.requiredSkills?.map(rs => ({
      title: rs[0],
      key: rs[2],
    })) || []
  )
  const [location, setLocation] = useState<string>(props.userSearch?.location)
  const [locationCity, setLocationCity] = useState(props.userSearch?.locationCity)
  const [locationState, setLocationState] = useState(props.userSearch?.locationState)
  const [locationCountryKey, setLocationCountryKey] = useState(props.userSearch?.locationCountryKey)
  const [locationLongitude, setLocationLongitude] = useState(props.userSearch?.locationLongitude)
  const [locationLatitude, setLocationLatitude] = useState(props.userSearch?.locationLatitude)

  const [upsertUserSearch, upsertUserSearchMutation] = useMutation(gql`
    mutation ($input: UpsertUserSearchInput!) {
      upsertUserSearch(input: $input) {
        userSearch {
          id
          ${USER_SEARCH_FRAGMENT}
        }
      }
    }
  `)

  // const [placeholderIndex, setPlaceholderIndex] = useState<number>(0);
  // const placeholders = useMemo(() => {
  //   return seededShuffle(
  //     [
  //       `I need help setting up my marketing analytics`,
  //       `I'd like to meet other founders in the fintech industry`,
  //       `I need advice on how to find and pitch investors`,
  //       `I'm looking for a developer to build my mobile app`,
  //       `I need a graphic designer to create some social media ads`,
  //     ],
  //     hourSeed(),
  //   );
  // }, []);
  // const placeholder = useDelayedText(placeholders[placeholderIndex]);

  // useInterval(() => {
  //   if (naturalLanguagePrompt) {
  //     return
  //   }
  //   if (placeholderIndex === placeholders.length - 1) {
  //     setPlaceholderIndex(0)
  //   } else {
  //     setPlaceholderIndex(placeholderIndex + 1)
  //   }
  // }, 4000)

  useEffect(() => {
    const userSearch = props.userSearch
    if (userSearch) {
      setTitle(userSearch.title)
      setNaturalLanguagePrompt(userSearch.naturalLanguagePrompt)
      setRequiredSkills(
        userSearch.requiredSkills?.map(rs => ({
          title: rs[0],
          key: rs[2],
        })) || []
      )
      setLocation(userSearch.location)
      setLocationCity(userSearch.locationCity)
      setLocationState(userSearch.locationState)
      setLocationCountryKey(userSearch.locationCountryKey)
      setLocationLongitude(userSearch.locationLongitude)
      setLocationLatitude(userSearch.locationLatitude)
    }
  }, [JSON.stringify(props.userSearch)])

  const onSubmit = async ev => {
    ev.preventDefault()

    const result = await upsertUserSearch({
      variables: {
        input: {
          userSearchId: props.userSearch?.userSearchId || null,
          naturalLanguagePrompt,
          requiredSkillIds: requiredSkills.map(rs => rs.key),
          location,
          locationCity,
          locationState,
          locationCountryKey,
          locationLongitude,
          locationLatitude,
          reprocess: true,
        },
      },
    })

    if (result.error) {
      alert(`Error: ${result.error.toString()}`)
    }

    const userSearch = result.data?.upsertUserSearch?.userSearch
    if (userSearch) {
      router.push(`/members/matching/${userSearch.userSearchId}`)
    } else {
      alert('Error: userSearchId is empty.')
    }
  }

  return (
    <Box key={props.userSearch?.userSearchId || 'new'} borderRadius={'md'} marginTop={2} width={'full'}>
      {!props.userSearch ? (
        <>
          <Heading>Who do you want to connect to?</Heading>
          <Separator marginY={'0.75em'} />
        </>
      ) : (
        <>
          <VStack
            align={'flex-start'}
            bgColor={'colorPalette.container'}
            borderRadius={'md'}
            boxShadow={'sm'}
            gap={1}
            justify={'space-between'}
            marginBottom={'0.75em'}
            padding={4}
            width={'full'}
          >
            {/* <Icon as={LuSearchCheck} /> */}
            {props.userSearch.title && (
              <Text color={'colorPalette.fg/50'} fontSize={'2xs'} textStyle={'label'}>
                Current Search:
              </Text>
            )}
            <Heading size={'md'}>{props.userSearch.title || 'Member Matching'}</Heading>
            {props.userSearch.userMatches.pagination.itemCount > 0 && (
              <Badge marginTop={'2'} paddingX={'2'} paddingY={'1'} size={'xs'} variant={'solid'}>
                {props.userSearch.userMatches.pagination.itemCount} Matches
              </Badge>
            )}
          </VStack>
          <Box paddingY={2}>
            <UserSearchEmailToggle userSearch={props.userSearch} />
          </Box>
          <Separator marginBottom={'1em'} />
        </>
      )}
      <form onSubmit={onSubmit}>
        <VStack align={'stretch'} gap={'1em'}>
          <Field.Root>
            <Field.Label color={'colorPalette.fg'} textStyle={'xs'}>
              {"Describe who you'd like to meet:"}{' '}
              {process.env.NEXT_PUBLIC_DEPLOYMENT_ENV === 'development' && (
                <Link
                  variant={'underline'}
                  onClick={() =>
                    setNaturalLanguagePrompt(
                      sample([
                        "I am looking for help with online ads for my dog grooming business. Specifically I'd like help with Facebook and Instagram ads.",
                        'I am raising a seed round of $1M. I need help finalizing my pitch deck and preparing for investor pitches.',
                        "I'd like to connect with others who like dogs",
                        "My app is buggy and I need help fixing it. It's built in React.JS with a PHP/Laravel back-end and hosted on AWS.",
                        "My company has 6 months of runway and I need help either growing sales or raising funds. I'm not sure what to effectively do next.",
                        "I need help building a mobile app in the sports betting space. I'd like it to work on both iOS and Android but don't care about the specific technologies. I could use a developer or advisor to help guide me in figuring out how to get it built.",
                        'I need a technical co-founder to build help with a robots idea',
                        "I need help growing sales and finding more leads. Competing brands are running social ads and I'm not sure how to get started.",
                      ])
                    )
                  }
                >
                  prefill
                </Link>
              )}
            </Field.Label>
            <AutoResizeTextarea
              minHeight={24}
              onChange={e => setNaturalLanguagePrompt(e.target.value)}
              fontSize={'xs'}
              // placeholder={`Ex: ${placeholder}`}
              value={naturalLanguagePrompt}
            />
          </Field.Root>

          <Field.Root>
            <Field.Label color={'colorPalette.fg'} textStyle={'xs'}>
              Prioritize Specific Skills:
            </Field.Label>
            <SkillsComboboxInput
              defaultValue={requiredSkills}
              comboboxTagInputProps={{
                allowMultipleSelections: true,
                inputProps: {},
              }}
              onChange={items => setRequiredSkills(items)}
            />
          </Field.Root>

          <Field.Root>
            <Field.Label color={'colorPalette.fg'} textStyle={'xs'}>
              Prioritize This Location:
            </Field.Label>
            <LocationInput
              placeTypes={['(cities)']}
              comboboxTagInputProps={{
                inputProps: {
                  title: 'location-filter',
                },
              }}
              defaultValue={{
                title: location,
                city: locationCity,
                state: locationState,
                countryISO: locationCountryKey,
                latitude: locationLatitude,
                longitude: locationLongitude,
              }}
              onChange={place => {
                setLocation(place?.title)
                setLocationCity(place?.city)
                setLocationState(place?.state)
                setLocationCountryKey(place?.countryISO)
                setLocationLongitude(place?.longitude)
                setLocationLatitude(place?.latitude)
              }}
            />
          </Field.Root>

          {upsertUserSearchMutation.error && (
            <Text>
              {'Error: '}
              {upsertUserSearchMutation.error.toString()}
            </Text>
          )}

          <Button loading={upsertUserSearchMutation.loading} type={'submit'} variant={'cta'}>
            {props.userSearch ? 'Update & Process' : 'See My Matches'} Search
          </Button>
        </VStack>
      </form>
    </Box>
  )
}

export const MatchEmailsExplainerModal = (props: { isOpen: boolean; onClose: () => void }) => {
  return (
    <Dialog.Root
      open={props.isOpen}
      onOpenChange={({ open }) => {
        if (!open) {
          props.onClose()
        }
      }}
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content backgroundColor={'container'} maxWidth={'container.sm'}>
          <Dialog.CloseTrigger />
          <Dialog.Body padding={8}>
            <Heading>New Match Email Updates</Heading>
            <Text marginY={4}>We'll notify you via email about new matches.</Text>
            <Button variant={'outline'} width={'full'} onClick={props.onClose}>
              Close
            </Button>
          </Dialog.Body>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  )
}
